/* eslint-disable complexity */
import React, { useMemo } from 'react';

import { notEmpty } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { CATEGORY_NAME_INDEXES, REPORT_SECTION } from '@futrli/predict/core/constants';
import { ForecastTree, UKForecastLayoutItem, UKForecastLayoutItemDisplayType } from '../types';
import { ForecastUpdating } from '../../components/forecast-updating';
import { getTopLevelStaticCategoryTreeNode } from '../utils/forecast-tree';
import { PredictionsTotalsPageWrapper } from '../predictions-totals-page-wrapper';
import { useOrganisationAccountNames } from '../hooks/useOrganisationAccountNames/useOrganisationAccountNames';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { useUKForecastGrid } from '../hooks/uk/useUKForecastGrid/useUKForecastGrid';
import { VIEW_TYPES } from '@futrli/predict/core/types';

const getLayout = (trees: Record<string, ForecastTree>, selectedReportSection: REPORT_SECTION) => {
  const tree = trees[selectedReportSection];
  const isCashFlow = selectedReportSection === REPORT_SECTION.CashFlow;
  const datasource = isCashFlow ? 'cash_flow' : 'profit_loss';
  const layout: UKForecastLayoutItem[] = [];

  const findNode = (category: CATEGORY_NAME_INDEXES) => {
    return getTopLevelStaticCategoryTreeNode(tree, category);
  };

  const incomeNode = findNode(CATEGORY_NAME_INDEXES.income);
  const costOfSalesNode = findNode(CATEGORY_NAME_INDEXES.cost_of_sales);
  const grossProfitNode = findNode('gross_profit' as CATEGORY_NAME_INDEXES);
  const grossProfitPercentNode = findNode('gross_profit_%' as CATEGORY_NAME_INDEXES);
  const expensesNode = findNode(CATEGORY_NAME_INDEXES.expenses);
  const operatingProfitNode = findNode('operating_profit' as CATEGORY_NAME_INDEXES);
  const operatingProfitPercentNode = findNode('operating_profit_%' as CATEGORY_NAME_INDEXES);
  const otherExpensesNode = findNode(CATEGORY_NAME_INDEXES.other_expenses);
  const otherIncomeNode = findNode(CATEGORY_NAME_INDEXES.other_income);
  const netProfitNode = findNode('net_profit' as CATEGORY_NAME_INDEXES);
  const netProfitPercentNode = findNode('net_profit_%' as CATEGORY_NAME_INDEXES);

  const showOperationalProfit = Boolean(otherIncomeNode?.children.length || otherExpensesNode?.children.length);

  if (incomeNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.income,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [incomeNode]
    });
  }
  if (costOfSalesNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.cost_of_sales,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [costOfSalesNode]
    });
  }
  if (grossProfitNode && !isCashFlow) {
    layout.push({
      category: 'gross_profit' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.calculation,
      nodes: [grossProfitNode, grossProfitPercentNode].filter(notEmpty)
    });
  }
  if (expensesNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.expenses,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [expensesNode],
      totalOptions: {
        padding: true
      }
    });
  }
  if (showOperationalProfit && operatingProfitNode && !isCashFlow) {
    layout.push({
      category: 'operating_profit' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.calculation,
      nodes: [operatingProfitNode, operatingProfitPercentNode].filter(notEmpty)
    });
  }
  if (otherIncomeNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.other_income,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [otherIncomeNode],
      totalOptions: {
        padding: true
      }
    });
  }
  if (otherExpensesNode) {
    layout.push({
      category: CATEGORY_NAME_INDEXES.other_expenses,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.totalWithAccounts,
      nodes: [otherExpensesNode]
    });
  }
  if (netProfitNode && !isCashFlow) {
    layout.push({
      category: 'net_profit' as CATEGORY_NAME_INDEXES,
      datasource,
      displayType: UKForecastLayoutItemDisplayType.calculation,
      nodes: [netProfitNode, netProfitPercentNode].filter(notEmpty)
    });
  }
  return layout;
};

export const ProfitAndLossV2 = () => {
  const accountNameMap = useOrganisationAccountNames();
  const {
    forecast,
    forecastReportV2: forecastReport,
    isLoading,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();
  const selectedReportSection = viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.ProfitLoss;
  const isCashFlow = selectedReportSection === REPORT_SECTION.CashFlow;

  const getLayoutFunc = useMemo(
    () => (trees: Record<string, ForecastTree>) => getLayout(trees, selectedReportSection),
    [selectedReportSection]
  );

  const { columns, dataGridRows } = useUKForecastGrid(
    forecast,
    forecastReport,
    accountNameMap,
    getLayoutFunc,
    !isCashFlow ? 'standard' : 'none'
  );

  if (!forecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <PredictionsTotalsPageWrapper
        columns={columns}
        totalAccountRows={dataGridRows}
        totalCashPredictRow={totalCashPredictRow}
        showDaysToPay
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
